/**
 * GL Mapper
 * Maps between API DTOs and Domain Entities
 */

import {
  GlEntity,
  GlsResultEntity,
  GlPaginationEntity,
  AccountTypeGroupsEntity,
  GlOperationResult,
} from "../../domain/entities/GlEntity";
import type { GlMapDTO, GlsListResponseDTO, AccountTypeGroupsResponseDTO } from "../models/GlDTOs";

export class GlMapper {
  static toGlEntity(dto: GlMapDTO): GlEntity {
    return {
      id: dto.id ?? 0,
      glId: dto.gl_id,
      glName: dto.gl_name,
      accountId: dto.account_id,
      accountType: dto.account_type,
      parentId: dto.parent_id ?? null,
      parentName: dto.parent_name ?? null,
      isActive: Number(dto.is_active) === 1,
      hasChildren: dto.has_children !== undefined ? Number(dto.has_children) === 1 : undefined,
      isGroup: dto.is_group !== undefined ? Number(dto.is_group) === 1 : false,
      invoice: Number(dto.invoice ?? 0) === 1,
      bill: Number(dto.bill ?? 0) === 1,
      isUnderReview: Number(dto.is_under_review ?? 0) === 1,
      createdAt: dto.created_at,
      updatedAt: dto.updated_at,
    };
  }

  static toPaginationEntity(
    total: number,
    limit: number,
    offset: number,
    hasMore: boolean
  ): GlPaginationEntity {
    return { total, limit, offset, hasMore };
  }

  static toGlsResultEntity(
    response: GlsListResponseDTO,
    requestedLimit: number,
    requestedOffset: number
  ): GlsResultEntity {
    const d = response.data;
    const gls = (d.gl_maps || []).map((m) => this.toGlEntity(m));
    const total = d.total ?? gls.length;
    const hasMore = d.has_more ?? false;
    const pagination: GlPaginationEntity = {
      total,
      limit: requestedLimit,
      offset: requestedOffset,
      hasMore,
    };
    return { gls, pagination };
  }

  static toAccountTypeGroupsEntity(response: AccountTypeGroupsResponseDTO): AccountTypeGroupsEntity {
    return {
      backend: response.data?.backend ?? "zoho",
      groups: response.data?.groups ?? {},
      labels: response.data?.labels ?? {},
      defaultParents: response.data?.default_parents ?? {},
    };
  }

  /**
   * Map create/update API response to GlOperationResult
   */
  static toCreateUpdateOperationResult(
    requiresReview: boolean,
    reviewId?: number,
    glMapDto?: GlMapDTO
  ): GlOperationResult {
    return {
      requiresReview,
      reviewId,
      gl: glMapDto ? this.toGlEntity(glMapDto) : undefined,
    };
  }

  /**
   * Map delete API response to GlOperationResult
   */
  static toDeleteOperationResult(
    requiresReview: boolean,
    reviewId?: number,
    glMapDto?: GlMapDTO
  ): GlOperationResult {
    return {
      requiresReview,
      reviewId,
      gl: glMapDto ? this.toGlEntity(glMapDto) : undefined,
    };
  }
}
